import React, { useState, useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { GameChoice } from '../../shared/schema';
import { PollClient } from '../poll-client';
import * as anchor from '@project-serum/anchor';
import { Connection, PublicKey } from '@solana/web3.js';

interface VotingModalProps {
  isOpen: boolean;
  onClose: () => void;
  showResults?: boolean;
}

interface VoteResult {
  game_choice: string;
  votes: string;
}

const WEEKLY_VOTE_PROGRAM_ID = new PublicKey("6TxniqSYQXCZjghxftkJH3znM7A7m1HBpwsQdh7vQogD");

function mapGameChoiceToValue(gameChoice: string | null): number {
  if (!gameChoice) return 0;
  
  switch(gameChoice) {
    case GameChoice.Chess: return 0;
    case GameChoice.Checkers: return 1;
    case GameChoice.Go: return 2;
    case GameChoice.Backgammon: return 3;
    default: return 0;
  }
}

export const VotingModal: React.FC<VotingModalProps> = ({ isOpen, onClose, showResults = false }) => {
  const [selectedGame, setSelectedGame] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<VoteResult[]>([]);
  const [totalVotes, setTotalVotes] = useState(0);
  const [hasVoted, setHasVoted] = useState(false);
  const [userVote, setUserVote] = useState<string | null>(null);
  const [arciumTxId, setArciumTxId] = useState<string | null>(null);
  const [arciumStatus, setArciumStatus] = useState<string | null>(null);
  const [shuffledGameChoices, setShuffledGameChoices] = useState<string[]>([]);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const [shouldRender, setShouldRender] = useState<boolean>(false);

  const { publicKey, signTransaction } = useWallet();
  const walletAddress = publicKey?.toString() || null;

  // Блокировка прокрутки страницы при открытом модальном окне
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Очистка при размонтировании компонента
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const shuffleArray = (array: any[]) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };
  
  useEffect(() => {
    if (isOpen) {
      setError(null);
      setArciumTxId(null);
      setArciumStatus(null);

      setShuffledGameChoices(shuffleArray(Object.values(GameChoice)));

      if (walletAddress) {
        checkUserVote(walletAddress);
      }

      if (showResults) {
        fetchResults();
      }
    }
  }, [isOpen, walletAddress, showResults]);

  // Управление анимациями открытия/закрытия
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Small delay to ensure DOM is ready before animation
      const timer = setTimeout(() => setIsAnimating(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsAnimating(false);
      // Wait for animation to complete before unmounting
      const timer = setTimeout(() => setShouldRender(false), 150);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);
  
  const checkUserVote = async (address: string) => {
    setIsLoading(true);
    try {
      const response = await fetch(`/api/game-vote/${address}`);
      
      if (response.status === 404) {
        setHasVoted(false);
        setUserVote(null);
        return;
      }
      
      if (!response.ok) {
        const text = await response.text();
        return;
      }
      
      const data = await response.json();
      
      setHasVoted(true);
      setUserVote(data.game_choice);
      if (showResults) {
        fetchResults();
      }
    } catch (err) {
      setHasVoted(false);
      setUserVote(null);
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchResults = async () => {
    try {
      const response = await fetch(`/api/game-vote-results`);
      if (!response.ok) {
        const text = await response.text();
        throw new Error('Failed to fetch results');
      }
      const data = await response.json();
      
      setResults(data);
      
      const total = data.reduce((sum: number, item: VoteResult) => {
        return sum + parseInt(item.votes);
      }, 0);
      
      setTotalVotes(total);
    } catch (err) {
      setError('Failed to fetch voting results');
    }
  };
  
  const handleSelectGame = (game: string) => {
    setSelectedGame(game);
  };
  
  const handleSubmitVote = async () => {
    if (!selectedGame || !walletAddress || !publicKey || !signTransaction) {
      setError('Please select a game and connect your wallet');
      return;
    }
    
    if (showResults) {
      setError('Voting is closed');
      return;
    }
    
    setError(null);
    setIsSubmitting(true);
    setArciumStatus("Preparing transaction...");
    
    try {
      const connection = new Connection('https://api.devnet.solana.com', 'confirmed');
      
      const provider = new anchor.AnchorProvider(
        connection,
        {
          publicKey,
          signTransaction,
          signAllTransactions: signTransaction
        },
        { commitment: 'confirmed' }
      );
      
      const pollClient = new PollClient(connection, provider.wallet, WEEKLY_VOTE_PROGRAM_ID);

      setArciumStatus("Initializing system...");

      try {
        await pollClient.initializeVoteSystem();

        setArciumStatus("Preparing transaction...");
        
        const pollId = await pollClient.findAvailablePollId();
        
        const messages = [
            "Initializing blockchain system...",
            "Sending vote to blockchain...",
            "Waiting for transaction confirmation...",
            "Performing confidential computation..."
        ];
        
        const signature = await pollClient.createNewPoll(pollId, "Which game do you prefer?");

        setArciumStatus(messages[2]);

        const txSignature = pollClient.getLastTransactionSignature() || signature;

        setArciumStatus(messages[3]);
        
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        setArciumTxId(txSignature);
        setArciumStatus("Vote registered successfully!");
        
        try {
          const response = await fetch(`/api/game-vote`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              wallet_address: walletAddress,
              game_choice: selectedGame,
              arcium_tx_id: txSignature
            })
          });
          
          if (!response.ok) {
            const text = await response.text();
          }
        } catch (apiError) {
        }
        
        setHasVoted(true);
        setUserVote(selectedGame);
      } catch (err: any) {
        console.error("Transaction error:", err);
        
        if (err.message && (
          err.message.includes("rejected") ||
          err.message.includes("cancelled") ||
          err.message.includes("User rejected")
        )) {
          setError("Transaction was cancelled by user");
        } else if (err.message && (
          err.message.includes("insufficient") ||
          err.message.includes("not enough") ||
          err.message.includes("balance") ||
          err.message.includes("funds") ||
          err.message.includes("SOL") ||
          err.message.includes("lamports") ||
          err.message.includes("fee") ||
          err.message.includes("cost") ||
          err.message.includes("payment") ||
          err.message.includes("account") ||
          err.message.includes("debit an account") ||
          err.message.includes("no record of a prior credit") ||
          err.message.toLowerCase().includes("insufficient") ||
          err.message.toLowerCase().includes("balance") ||
          err.message.toLowerCase().includes("funds")
        )) {
          setError("Insufficient balance. Please get some SOL from the faucet.");
        } else if (err.message && err.message.includes("0x0")) {
          setError("Vote couldn't be processed - try again later (error code: 0x0)");
        } else if (err.message && err.message.includes("compute budget exceeded")) {
          setError("Transaction requires more compute units than available");
        } else {
          setError("Transaction error occurred");
        }
      }
    } catch (err: any) {
      setError("Transaction error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const getPercentage = (game: string): number => {
    if (totalVotes === 0) return 0;
    const gameResult = results.find(r => r.game_choice === game);
    if (!gameResult) return 0;
    return Math.round((parseInt(gameResult.votes) / totalVotes) * 100);
  };
  
  const getVoteCount = (game: string): number => {
    const gameResult = results.find(r => r.game_choice === game);
    return gameResult ? parseInt(gameResult.votes) : 0;
  };

  const getLeadingGame = (): string | null => {
    if (results.length === 0) return null;

    let maxVotes = 0;
    let leadingGame = null;

    for (const result of results) {
      const votes = parseInt(result.votes);
      if (votes > maxVotes) {
        maxVotes = votes;
        leadingGame = result.game_choice;
      }
    }

    return leadingGame;
  };
  
  if (!shouldRender) return null;
  
  const renderVotingContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-purple-500"></div>
          <span className="ml-3 text-gray-300">Checking your vote status...</span>
        </div>
      );
    }
    
    if (showResults) {
      return (
        <div className="space-y-4">
          <p className="text-yellow-400 mb-4 font-bold text-center">
            Voting is now closed. No more votes are being accepted.
          </p>
          
          <h3 className="text-lg font-semibold text-white">Current Results:</h3>
          
          <div className="space-y-3">
            {Object.values(GameChoice).map(game => (
              <div key={game} className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className={`${game === userVote ? 'font-bold text-purple-400' : 'text-gray-300'}`}>
                    {game}
                  </span>
                  <span className="text-gray-300">{getPercentage(game)}% ({getVoteCount(game)} votes)</span>
                </div>
                <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${
                      game === userVote ? 'bg-purple-500' :
                      game === getLeadingGame() ? 'bg-orange-400' :
                      'bg-purple-700'
                    }`}
                    style={{ width: `${getPercentage(game)}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
          
          <p className="text-gray-400 text-sm mt-4">
            Total votes: {totalVotes}
          </p>
        </div>
      );
    }
    
    if (hasVoted) {
      return (
        <div className="space-y-4">
          <div className="text-center mb-4">
            <p className="text-green-400 font-bold">
              Your vote has been counted!
            </p>
            <p className="text-purple-300 mt-2">
              Results will be available on Thursday, stay &lt;encrypted&gt;
            </p>
          </div>
          
          {arciumTxId && (
            <p className="text-sm text-purple-400 mb-2 text-center">
              Transaction: <a 
                href={`https://explorer.solana.com/tx/${arciumTxId}?cluster=devnet`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="underline hover:text-purple-300"
              >
                {arciumTxId.slice(0, 8)}...{arciumTxId.slice(-8)}
              </a>
            </p>
          )}
        </div>
      );
    }
    
    return (
      <>
        <div className="space-y-3 mb-6">
          {shuffledGameChoices.map(game => (
            <div 
              key={game}
              onClick={() => !isSubmitting && handleSelectGame(game)}
              className={`p-4 rounded-lg transition-all border ${
                selectedGame === game
                  ? 'border-purple-500 bg-purple-900/50'
                  : 'border-purple-500/30 hover:border-purple-500/70'
              } ${isSubmitting ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
            >
              <div className="flex items-center">
                <div className={`min-w-[16px] min-h-[16px] w-4 h-4 rounded-full mr-3 flex-shrink-0 ${
                  selectedGame === game
                    ? 'bg-purple-500'
                    : 'bg-purple-500/30'
                }`}>
                  {selectedGame === game && (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  )}
                </div>
                <span className="text-white">{game}</span>
              </div>
            </div>
          ))}
        </div>
        
        <button
          onClick={handleSubmitVote}
          disabled={!selectedGame || isSubmitting || !walletAddress}
          className={`w-full py-3 px-4 border-2 border-purple-500/80 font-medium ${
            selectedGame && walletAddress && !isSubmitting
              ? 'btn-textured text-white'
              : 'btn-textured-secondary text-white/50 cursor-not-allowed opacity-50'
          } transition-colors`}
        >
          {isSubmitting ? arciumStatus || 'Submitting...' : 'Submit Vote'}
        </button>
        
        {error && (
          <p className="mt-4 text-red-500 text-sm">{error}</p>
        )}
        
        {!walletAddress && (
          <p className="mt-4 text-yellow-500 text-sm">
            Please connect your wallet to vote
          </p>
        )}
      </>
    );
  };
  
  return (
    <div className="fixed inset-0 z-50">
      <style>{`
        .modal-content {
          opacity: 0;
          transform: scale(0.5);
          transition: all 0.15s ease-out;
        }

        .modal-content.entering {
          opacity: 1;
          transform: scale(1);
        }

        .modal-content.exiting {
          opacity: 0;
          transform: scale(0.5);
        }
      `}</style>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="fixed inset-0 flex items-center justify-center">
        <div
          className={`bg-black/80 rounded-xl shadow-xl border border-purple-800/50 p-6 max-w-md w-full mx-4 animate-border-glow modal-content ${
            isAnimating ? 'entering' : 'exiting'
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-white">Weekly Game Vote</h2>
            <button 
              onClick={onClose}
              className="text-purple-400 hover:text-white transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          
          <div className="mb-6">
            {!showResults && !hasVoted && (
              <p className="text-gray-300 mb-4">
                Choose which game you want to play this week:
              </p>
            )}
            
            {renderVotingContent()}
          </div>
          
          <div className="mt-4 pt-4 border-t border-gray-700/50">
            <p className="text-gray-400 text-xs leading-relaxed">
              This voting system uses Arcium's confidential computing capabilities to ensure truly anonymous voting on Solana. Your individual votes remain private while still allowing for verifiable results. Arcium uses Multi-Party Computation (MPC) to achieve a trust-minimized setup for confidential voting, addressing a key challenge in prediction markets where transparent voting can lead to cascade effects that distort outcomes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}; 